import React, { useMemo, useState, useCallback } from 'react';

import {
  HStack, ModalCloseButton, ModalContent, Text, useToast,
} from '@chakra-ui/react';
import { useMutation } from 'react-query';
import { useParams, useHistory } from 'react-router-dom';

import { Button, Icon, Modal } from 'components';
import { modals } from 'constants/table';
import useActions from 'hooks/useActions';

interface DeleteModalProps {
  onSuccess?: () => void
  fallbackContent?: string
}

export default function DeleteModal({ onSuccess = () => null, fallbackContent }:DeleteModalProps) {
  const [isSuccess, setIsSuccess] = useState(false);
  const { content } = useParams<GeneralRoute>();
  const { push } = useHistory();
  const toast = useToast();
  const {
    onClear, action, confirmDelete, row,
  } = useActions();
  const { mutate, isLoading } = useMutation(confirmDelete,
    {
      onSuccess: () => {
        setIsSuccess(true);
        onClear();
        onSuccess();
      },
      onError: (error: any) => {
        toast({
          title: 'Ocorreu um erro.',
          description: error.message,
          status: 'error',
          duration: 5000,
          position: 'top-right',
          isClosable: true,
        });
        onClear();
      },
    });

  const baseModals = useMemo(() => modals[content ?? fallbackContent], [content, fallbackContent]);

  const modalActions = useMemo(() => ({
    onClear,
    mutate,
    success: () => {
      setIsSuccess(false);
      onClear();
    },
    'filter role': () => {
      onClear();
      push(`/table/users?role.name=${row?.name}`);
    },
  }), [row]);

  const isBlocked = useMemo(() => {
    if (content === 'roles') {
      return row?.relatedUsers > 0;
    }
    return false;
  }, [content, row]);
  function ConfirmationContent() {
    const {
      title, message, secondaryButton, primaryButton,
    } = baseModals.confirmation || {};

    return (
      <>
        <Text
          variant="largeBold"
          mx={8}>
          {title(row)}
        </Text>
        <Text
          variant="smallRegular"
          mt={5}>
          {message(row)}
        </Text>
        <HStack mt={6}>
          {secondaryButton && (
            <Button
              onClick={modalActions[secondaryButton.action]}
              variant="secondary.teal">
              <Text variant="smallBold">
                {secondaryButton.text}
              </Text>
            </Button>
          )}
          {primaryButton && (
            <Button
              onClick={modalActions[primaryButton.action]}
              variant="primary.blue"
              isLoading={isLoading}>
              <Text variant="smallBold">
                {primaryButton.text}
              </Text>
            </Button>
          )}
        </HStack>
      </>
    );
  }

  function BlockContent() {
    const {
      title, message, secondaryButton, primaryButton,
    } = baseModals.block || {};
    return (
      <>
        <Text
          variant="largeBold"
          mx={8}>
          {title(row)}
        </Text>
        <Text
          variant="smallRegular"
          mt={5}>
          {message(row)}
        </Text>
        <HStack mt={6}>
          {secondaryButton && (
            <Button
              onClick={modalActions[secondaryButton.action]}
              variant="secondary.teal">
              <Text variant="smallBold">
                {secondaryButton.text}
              </Text>
            </Button>
          )}
          {primaryButton && (
            <Button
              onClick={modalActions[primaryButton.action]}
              variant="primary.blue"
              isLoading={isLoading}>
              <Text variant="smallBold">
                {primaryButton.text}
              </Text>
            </Button>
          )}
        </HStack>
      </>
    );
  }

  function SuccessContent() {
    const {
      title, message, primaryButton,
    } = baseModals.success || {};
    return (
      <>
        <Icon
          name="check-circle"
          family="fal"
          color="primary.default"
          size="5xl"
        />
        <Text
          mt={3}
          variant="xLargeBold"
          mx={8}>
          {title(row)}
        </Text>
        <Text
          variant="smallRegular"
          mt={5}>
          {message(row)}
        </Text>
        <Button
          mt={6}
          variant="primary.blue"
          onClick={modalActions[primaryButton.action]}>
          <Text variant="smallBold">{primaryButton.text}</Text>
        </Button>
      </>
    );
  }

  const Content = useCallback(() => {
    if (isBlocked) {
      return <BlockContent />;
    } if (isSuccess) {
      return <SuccessContent />;
    }
    return <ConfirmationContent />;
  }, [isBlocked, isSuccess, row]);

  return (
    <Modal
      isOpen={action === 'delete' || isSuccess}
      onClose={onClear}>
      <ModalContent
        alignSelf="center"
        alignItems="center"
        textAlign="center"
        p={6}>
        <ModalCloseButton onClick={() => {
          onClear();
          setIsSuccess(false);
        }}
        />
        <Content />
      </ModalContent>
    </Modal>
  );
}
